"use client";

import { CSSProperties, useLayoutEffect, useRef, useState } from "react";
import classNames from "classnames";

import { MarqueeProps } from "./types";

import "./styles.scss";

const DEFAULT_SPEED = 40;
const DEFAULT_GAP = 40;
const MIN_REPEAT = 2;

const getGapPx = (marqueeNode: HTMLElement, gap?: number) =>
  gap ??
  (parseFloat(getComputedStyle(marqueeNode).getPropertyValue("--marquee-gap")) || DEFAULT_GAP);

export const Marquee = ({
  items,
  speed = DEFAULT_SPEED,
  gap,
  repeat = MIN_REPEAT,
  pauseOnHover = false,
  className,
}: MarqueeProps) => {
  const marqueeRef = useRef<HTMLDivElement>(null);
  const groupRef = useRef<HTMLDivElement>(null);
  const [shift, setShift] = useState(0);

  useLayoutEffect(() => {
    const groupNode = groupRef.current;
    const marqueeNode = marqueeRef.current;
    if (!groupNode || !marqueeNode) return;

    const updateShift = () => {
      const groupWidth = groupNode.getBoundingClientRect().width;
      setShift(groupWidth + getGapPx(marqueeNode, gap));
    };

    const observer = new ResizeObserver(updateShift);
    observer.observe(groupNode);
    updateShift();

    return () => observer.disconnect();
  }, [items, gap]);

  const copies = Math.max(MIN_REPEAT, repeat);

  const style = {
    "--marquee-speed": `${speed}s`,
    ...(gap !== undefined ? { "--marquee-gap": `${gap}px` } : {}),
    "--marquee-shift": `${shift}px`,
  } as CSSProperties;

  const classnames = classNames("marquee", {
    "marquee--pause-on-hover": pauseOnHover,
    "marquee--ready": shift > 0,
    [className as string]: !!className,
  });

  return (
    <div
      ref={marqueeRef}
      className={classnames}
      data-testid="marquee"
      style={style}
    >
      <div
        className="marquee__track"
        data-testid="marquee-track"
      >
        {Array.from({ length: copies }).map((_, copyIndex) => (
          <div
            key={copyIndex}
            ref={copyIndex === 0 ? groupRef : undefined}
            className="marquee__group"
            aria-hidden={copyIndex > 0}
          >
            {items.map((item, index) => (
              <div
                key={index}
                className="marquee__item"
              >
                {item}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
